import { Calendar } from 'primeng/calendar';

let isCalendarFocusGuardPatched = false;

/**
 * Guard temporal para PrimeNG Calendar 19.0.7:
 * evita crash al enfocar botones prev/next durante navegación del overlay.
 */
export const applyPrimeCalendarFocusGuard = (): void => {
  if (isCalendarFocusGuardPatched) {
    return;
  }

  const prototype = Calendar.prototype as unknown as {
    updateFocus?: () => void;
    initFocusableCell?: () => void;
    bindDocumentResizeListener?: () => void;
    bindDocumentClickListener?: () => void;
    contentViewChild?: { nativeElement?: HTMLElement };
    el?: { nativeElement?: HTMLElement };
    renderer?: { listen?: (target: unknown, eventName: string, callback: (...args: unknown[]) => void) => (() => void) | undefined };
    window?: Window;
    touchUI?: boolean;
    documentResizeListener?: (() => void) | null;
    documentClickListener?: (() => void) | null;
    onWindowResize?: () => void;
    isOutsideClicked?: (event: Event) => boolean;
    overlayVisible?: boolean;
    hideOverlay?: () => void;
    onClickOutside?: { emit: (event: Event) => void };
    cd?: { markForCheck: () => void };
    zone?: { runOutsideAngular: (fn: () => void) => void; run: (fn: () => void) => void };
    document?: Document;
    navigationState?: { backward?: boolean; button?: boolean } | null;
    _focusKey?: string | null;
  };

  const originalUpdateFocus = prototype.updateFocus;
  const originalBindDocumentResizeListener = prototype.bindDocumentResizeListener;
  const originalBindDocumentClickListener = prototype.bindDocumentClickListener;

  if (typeof originalUpdateFocus !== 'function') {
    return;
  }

  prototype.updateFocus = function updateFocusWithGuard(this: typeof prototype): void {
    if (this.navigationState?.button) {
      if (typeof this.initFocusableCell === 'function') {
        this.initFocusableCell();
      }

      const content = this.contentViewChild?.nativeElement;
      const selector = this.navigationState.backward
        ? '.p-datepicker-prev, .p-datepicker-prev-button'
        : '.p-datepicker-next, .p-datepicker-next-button';

      const target = content?.querySelector<HTMLElement>(selector) ?? null;
      target?.focus();

      this.navigationState = null;
      this._focusKey = null;
      return;
    }

    originalUpdateFocus.call(this);
  };

  if (typeof originalBindDocumentResizeListener === 'function') {
    prototype.bindDocumentResizeListener = function bindDocumentResizeListenerWithGuard(this: typeof prototype): void {
      if (this.documentResizeListener || this.touchUI) {
        return;
      }

      const ownerDocument = this.el?.nativeElement?.ownerDocument;
      const targetWindow = this.window ?? ownerDocument?.defaultView ?? this.document?.defaultView ?? globalThis.window;

      if (!targetWindow || typeof targetWindow.addEventListener !== 'function') {
        return;
      }

      if (!this.renderer?.listen || typeof this.onWindowResize !== 'function') {
        return;
      }

      this.documentResizeListener = this.renderer.listen(targetWindow, 'resize', this.onWindowResize.bind(this)) ?? null;
    };
  }

  if (typeof originalBindDocumentClickListener === 'function') {
    prototype.bindDocumentClickListener = function bindDocumentClickListenerWithGuard(this: typeof prototype): void {
      if (this.documentClickListener) {
        return;
      }

      const documentTarget = this.el?.nativeElement?.ownerDocument ?? this.document;

      if (!documentTarget || typeof documentTarget.addEventListener !== 'function') {
        return;
      }

      if (!this.zone || !this.renderer?.listen || !this.isOutsideClicked || !this.hideOverlay) {
        return;
      }

      this.zone.runOutsideAngular(() => {
        this.documentClickListener = this.renderer?.listen?.(documentTarget, 'mousedown', (...args: unknown[]) => {
          const event = args[0] as Event;
          if (this.isOutsideClicked?.(event) && this.overlayVisible) {
            this.zone?.run(() => {
              this.hideOverlay?.();
              this.onClickOutside?.emit(event);
              this.cd?.markForCheck();
            });
          }
        }) ?? null;
      });
    };
  }

  isCalendarFocusGuardPatched = true;
};
